import {
  AlertColor,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  styled,
  TextField,
  Theme,
  useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { globalTheme } from "../../../common/theme";
import createStyles from "@mui/styles/createStyles";
import axios from "axios";
import React from "react";
import { PermissionModule, StaffPermission } from "./StaffAccessories";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import Title from "../../component/Title";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { GridCellParams, GridColumns, GridToolbarContainer } from "@mui/x-data-grid";
import { Save, Search } from "@mui/icons-material";
import { renewToken } from "../../../services/AuthService";
import { useConstructor } from "../../../common/UseConstructor";
import { UserActions } from "../../../models/enums/UserActions";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
  })
);

const StyledBox = styled(Box)(({ theme }) => ({
  height: "100%",
  width: "100%",
  "& .super-app-theme--cell": {
    backgroundColor: "rgba(41, 67, 63, 0.90)"
  },
}));

export function PermissionManagement() {
    const style = { ...useStyles(), ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');

    const [loading, setLoading] = React.useState<boolean>(false);

    const hasChanges = React.useRef<boolean>(false);

    const [permissionModules, setPermissionModules] = React.useState<PermissionModule[]>([]);
    const [gridData, setGridData] = React.useState<StaffPermission[]>([]);
    const [selectedPermissionModule, setSelectedPermissionModule] = React.useState<PermissionModule>();

    //Grid columns
    const moduleColumns: GridColumns = [
      { field: 'description', headerName: 'Module Name', flex: 1, type: 'string',
        cellClassName: (params: GridCellParams<number>) => {
          if (params.value == null) {
            return "";
          }

          if (params.row.id == 0)
            return "super-app-theme--cell";
          else
            return "";
        }
      }
    ];

    const columns: GridColumns = [
      { field: 'staffName', headerName: 'Staff Name', minWidth: 250, type: 'string' },
      { field: 'read', headerName: 'Open', disableColumnMenu: true, sortable: false, 
        width: 60, minWidth: 60,
        renderCell: (params: GridCellParams) => {
          return (
            <FormControlLabel
              control={<Checkbox checked={params.value} onChange={(e) => {
                onCheckBoxChanged(params, e.target.checked, UserActions.READ);
              }}/>}
              label=""
            />
          );
        }
      },
      { field: 'add', headerName: 'Add', disableColumnMenu: true, sortable: false, 
        width: 50, minWidth: 50,
        renderCell: (params: GridCellParams) => {
          return (
            <FormControlLabel
              control={<Checkbox checked={params.value} onChange={(e) => {
                onCheckBoxChanged(params, e.target.checked, UserActions.INSERT);
              }}/>}
              label=""
            />
          );
        }
      },
      { field: 'edit', headerName: 'Edit', disableColumnMenu: true, sortable: false, 
        width: 50, minWidth: 50,
        renderCell: (params: GridCellParams) => {
          return (
            <FormControlLabel
              control={<Checkbox checked={params.value} onChange={(e) => {
                onCheckBoxChanged(params, e.target.checked, UserActions.UPDATE);
              }}/>}
              label=""
            />
          );
        }
      },
      { field: 'delete', headerName: 'Delete', disableColumnMenu: true, sortable: false, 
        width: 70, minWidth: 70,
        renderCell: (params: GridCellParams) => {
          return (
            <FormControlLabel
              control={<Checkbox checked={params.value} onChange={(e) => {
                onCheckBoxChanged(params, e.target.checked, UserActions.DELETE);
              }}/>}
              label=""
            />
          );
        }
      },
      { field: 'dummy', headerName: '', flex: 3, disableColumnMenu: true, sortable: false }
    ];
   
    const [searchText, setSearchText] = React.useState("");

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

    function onCheckBoxChanged(params: GridCellParams, checked: boolean, actionType: UserActions) {
      let tmpData = gridData;
      let index = tmpData.findIndex(x => x.staffId === params.row.staffId);
      switch(actionType) {
        case UserActions.READ:
          tmpData[index].read = checked;
          break;
        case UserActions.INSERT:
          tmpData[index].add = checked;
          break;
        case UserActions.UPDATE:
          tmpData[index].edit = checked;
          break;
        case UserActions.DELETE:
          tmpData[index].delete = checked;
          break;
      }
      setGridData(tmpData);
    }

    function PermissionToolBar() {
      return (
        <GridToolbarContainer style={{ display: 'flex' }}>
          <Button style={{ width: tinyScreen ? "60px" : (smallScreen ? "75px" : "90px"), minWidth: tinyScreen ? "60px" : (smallScreen ? "75px" : "90px"), margin: 10 }}
            variant="contained" size="small" color="secondary" startIcon={<Save />} onClick={() => SavePermission()}>Save</Button>
        </GridToolbarContainer>
      );
    }


    function LoadData() {
      setLoading(true);

      axios.get<PermissionModule[]>('Api/V1/Permission', {
        headers: {
          'Content-Encoding': 'gzip'
        }
      })
        .then((result) => {
          setPermissionModules(result.data)
          setLoading(false);
        },
          (error) => {
            setPermissionModules([]);
            setLoading(false);
            console.log(error);
            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
          }
        )
        .catch((error) => {
          setLoading(false);
          console.log(error);
          showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
        });
    }
  
    function ReloadPermissions(objectId: number) {
      setLoading(true);

      axios.get<StaffPermission[]>('Api/V1/Permission/Object/' + objectId.toString(), {
          headers: {
              'Content-Encoding': 'gzip'
          }
      })
          .then((result) => {
              setGridData(result.data)
              setLoading(false);
          },
              (error) => {
                  setGridData([]);
                  setLoading(false);
                  console.log(error);
                  showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
              }
          )
          .catch((error) => {
              console.log(error);
              showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
          });
    }

    function SavePermission()
    {
      if (selectedPermissionModule)
      {
        let permissions = [...gridData];
        permissions.forEach(x => {
          x.action = x.action ?? "";
          x.objectId = x.objectId ?? selectedPermissionModule.objectId;
          x.description = x.description ?? selectedPermissionModule.description;
        });
        axios.post<StaffPermission[]>('Api/V1/Permission/Object', permissions).then(
          (result) => {
            setGridData(result.data);
  
            showSnackBar("Permission saved successfully.", "", "success");
            hasChanges.current = false;
          },
          (error) => {
            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
          }
        );
      }
    }

    useConstructor(() => Initialise())
    function Initialise() {
      renewToken();
      
      hasChanges.current = false;
      LoadData();
    }

    return (
        <div className={style.PageMain}>
            <Title pageTitle="Permission Management" />
            <div className={style.PageContent}>
              <div className={style.ModalContent}>
                <div className={style.PageFieldsFullHeight}>
                  <div className={style.ModalContentFieldOneQuarterWidth}>
                  <StyledBox>
                    <DataGridPro
                      getRowId={(row) => row.objectId}
                      rows={permissionModules}
                      columns={moduleColumns}
                      loading={loading}
                      rowHeight={tinyScreen ? 20 : (smallScreen ? 24 : 28)}
                      onRowClick={(params) => {
                        ReloadPermissions(params.row.objectId);
                        setSelectedPermissionModule(params.row);
                      }}
                    />
                    </StyledBox>
                  </div>
                  <div className={style.ModalContentFieldThreeQuarterWidth}>
                    <DataGridPro
                      getRowId={(row) => row.staffId}
                      rows={gridData}
                      columns={columns}
                      pageSize={100}
                      pagination
                      loading={loading}
                      components={{
                        Toolbar: PermissionToolBar
                      }}
                      rowHeight={tinyScreen ? 22 : (smallScreen ? 26 : 30)}
                    />
                  </div>
                </div>
              </div>
            </div>

          <SnackBarMessage {...snackSettings} />

        </div>
    );


  }

